import {
  Chip,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  TextField,
  Tooltip,
} from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { cn } from 'src/utils/styles';
import { match } from 'ts-pattern';
import { useDebounceCallback } from 'usehooks-ts';
import TablerIconSearch from '~icons/tabler/search';
import { useSearchMutation } from '#/api';
import { FilePathTruncate } from '#/components/Utils';
import {
  GitHubFileIcon,
  type GitHubRepository,
  GitHubRepositoryMultiAutocomplete,
} from '#/features/github';
import { getChatContextGitHubRepositoryFiles } from './context-data';
import { getChatContextGitHubRepositories } from './context-data';
import type {
  ChatContextGitHubRepositoryFile,
  ChatContextItem,
  OnChangeChatContextCallback,
} from './types';

export interface ChatContextMenuCodeProps {
  /**
   * The values to display.
   */
  context: ChatContextItem[];
  /**
   * The callback to call when the context changes.
   */
  onChangeContext: OnChangeChatContextCallback;
}

function ChatContextMenuCode({
  context,
  onChangeContext,
}: ChatContextMenuCodeProps) {
  const searchMutation = useSearchMutation();
  const [focused, setFocused] = React.useState<boolean>(false);

  const fileContext = React.useMemo(() => {
    return getChatContextGitHubRepositoryFiles(context);
  }, [context]);

  const repoContext = React.useMemo(() => {
    return getChatContextGitHubRepositories(context);
  }, [context]);

  const canSearch = React.useMemo(() => {
    return repoContext.length > 0;
  }, [repoContext]);

  const results = React.useMemo(() => {
    if (!searchMutation.data) {
      return [];
    }

    return searchMutation.data.githubRepositoryFiles;
  }, [searchMutation.data]);

  const codeSearchPlaceholder = React.useMemo(() => {
    return match(repoContext)
      .when(
        (r) => r.length === 0,
        () => 'Select at least 1 repository'
      )
      .when(
        (r) => r.length === 1,
        (r) => `Start typing to search ${r[0].name}`
      )
      .otherwise(
        () => `Start typing to search ${repoContext.length} repositories`
      );
  }, [repoContext]);

  const handleInputKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key === 'ArrowDown' &&
        searchMutation.data?.githubRepositoryFiles &&
        searchMutation.data.githubRepositoryFiles.length > 0
      ) {
        e.preventDefault();
        e.stopPropagation();

        setFocused(true);
      }
    },
    [searchMutation.data]
  );

  const handleAddFile = React.useCallback(
    (file: ChatContextGitHubRepositoryFile) => {
      onChangeContext([...repoContext, ...fileContext, file]);
    },
    [onChangeContext, fileContext, repoContext]
  );
  const handleChangeRepositories = React.useCallback(
    (repos: GitHubRepository[]) => {
      onChangeContext([
        ...fileContext,
        ...repos.map((r) => ({
          id: r.id,
          name: r.name,
          owner: r.owner,
          defaultBranch: r.defaultBranch,
        })),
      ]);
    },
    [onChangeContext, fileContext]
  );

  const handleSearch = useDebounceCallback((query: string) => {
    if (!canSearch) {
      return;
    }

    searchMutation.mutate({
      data: {
        query,
        filters: {
          githubRepositoryIds: repoContext.map((r) => r.id.toString()),
        },
        include: ['githubRepositoryFiles'],
      },
    });
  }, 500);

  return (
    <div
      id='chat-context-menu-code'
      className='w-full md:w-[500px] max-w-[500px] min-h-[400px]'
    >
      <div className='px-2 w-full mb-1 mt-1'>
        <GitHubRepositoryMultiAutocomplete
          id='context-search-code'
          className='mb-3'
          defaultValue={repoContext as GitHubRepository[]}
          onChange={handleChangeRepositories}
        />
        <div className='relative w-full'>
          <Tooltip
            enterDelay={700}
            enterNextDelay={700}
            title={match(canSearch)
              .with(
                false,
                () => 'You need to select at least one repository to search'
              )
              .with(true, () => undefined)
              .exhaustive()}
            placement='bottom'
            arrow
          >
            <TextField
              size='small'
              fullWidth
              disabled={!canSearch}
              onChange={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleSearch(event.target.value);
              }}
              onKeyDown={handleInputKeyDown}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <TablerIconSearch />
                    </InputAdornment>
                  ),
                },
              }}
              placeholder={codeSearchPlaceholder}
            />
          </Tooltip>
          <LinearProgress
            className={cn('absolute bottom-0 left-1 right-1 h-1', {
              invisible: searchMutation.status !== 'pending',
            })}
            color='primary'
          />
        </div>
      </div>
      <MenuList className='max-h-[600px] overflow-y-auto'>
        {results.length === 0 && (
          <div className='px-2 py-1 text-xs text-center'>No results found</div>
        )}
        {canSearch &&
          results.map((result, i) => {
            return (
              <MenuItem
                key={result.path}
                onClick={() => {
                  handleAddFile(result);
                }}
                disabled={isSelectedFile(result, fileContext)}
                autoFocus={focused && i === 0}
              >
                <ListItemIcon className='min-w-[24px]'>
                  <GitHubFileIcon
                    fileExtension={result.name.split('.').pop() ?? ''}
                    iconType='brand'
                  />
                </ListItemIcon>
                <ListItemText
                  primary={result.name}
                  className='w-full text-ellipsis text-nowrap overflow-hidden'
                />
                {result.path !== result.name && (
                  <div className='hidden md:block md:w-[200px] text-ellipsis text-nowrap overflow-hidden text-xs opacity-70'>
                    <FilePathTruncate
                      filePath={result.path}
                      maxCharacters={48}
                      hint='always'
                    />
                  </div>
                )}
                <div className='flex-1 min-h-1 min-w-2' />
                <div className='size-fit'>
                  <Chip
                    size='small'
                    label={`${result.githubRepository.name}`}
                  />
                </div>
              </MenuItem>
            );
          })}
      </MenuList>
    </div>
  );
}

export default ChatContextMenuCode;

/*
|------------------
| Internals
|------------------
*/

function isSelectedFile(
  result: ChatContextGitHubRepositoryFile,
  context: ChatContextGitHubRepositoryFile[]
) {
  return context.some(
    (c) =>
      `${c.githubRepository.id}/${c.path}` ===
      `${result.githubRepository.id}/${result.path}`
  );
}
